import { useState, useEffect } from "react";
import { ShieldCheck, ArrowsLeftRight, ChartBar, Warning, Check, ArrowClockwise, Trophy, ShareNetwork, Copy } from "@phosphor-icons/react";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Cell } from "recharts";
import axios from "axios";
import { API } from "@/App";
import { PlayerPhoto } from "@/components/PlayerPhoto";

const VERDICT_COLORS = { FAIR: "#4ade80", CLOSE: "#4ade80", UNEVEN: "#fbbf24", LOPSIDED: "#ff6b6b", EXTREME: "#ff3b30" };

export default function CommissionerDashboard({ leagueId, leagueName }) {
  const [tradeReview, setTradeReview] = useState(null);
  const [health, setHealth] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("trades");
  const [shareUrl, setShareUrl] = useState(null);
  const [sharing, setSharing] = useState(false);

  useEffect(() => {
    if (!leagueId) return;
    setLoading(true);
    Promise.all([
      axios.post(`${API}/commissioner/league-trade-review`, { league_id: leagueId }).then(r => setTradeReview(r.data)).catch(() => {}),
      axios.post(`${API}/commissioner/league-health`, { league_id: leagueId }).then(r => setHealth(r.data)).catch(() => {}),
    ]).finally(() => setLoading(false));
  }, [leagueId]);

  if (!leagueId) return null;

  if (loading) return (
    <div className="space-y-4 py-4">
      <div className="h-32 skeleton rounded-sm" />
      <div className="h-64 skeleton rounded-sm" />
    </div>
  );

  return (
    <div className="space-y-4" data-testid="commissioner-dashboard">
      <div className="flex items-center gap-2 mb-2">
        <ShieldCheck size={18} weight="bold" className="text-[#4ecdc4]" />
        <h2 className="font-heading text-lg text-white uppercase">COMMISSIONER VIEW</h2>
        <span className="text-[10px] text-[#555]">{leagueName}</span>
        <button onClick={async () => {
          setSharing(true);
          try {
            const res = await axios.post(`${API}/commissioner/share-report`, { league_id: leagueId });
            setShareUrl(res.data.url);
            navigator.clipboard?.writeText(res.data.url);
          } catch {}
          setSharing(false);
        }} className="ml-auto flex items-center gap-1 px-2.5 py-1 border border-[#333] rounded-sm text-[10px] text-[#888] hover:border-[#4ecdc4] hover:text-[#4ecdc4] transition-colors">
          {shareUrl ? <><Check size={10} className="text-[#4ade80]" /> Copied!</> : sharing ? <ArrowClockwise size={10} className="animate-spin" /> : <><ShareNetwork size={10} /> Share Report</>}
        </button>
      </div>

      <div className="flex gap-1 mb-4">
        {["trades", "health"].map(t => (
          <button key={t} onClick={() => setTab(t)} className={`px-3 py-1.5 text-[10px] font-bold uppercase tracking-wider rounded-sm transition-colors ${tab === t ? "bg-[#4ecdc4]/10 text-[#4ecdc4] border border-[#4ecdc4]/30" : "text-[#666] hover:text-white"}`}>{t === "trades" ? "Trade Review" : "League Health"}</button>
        ))}
      </div>

      {/* Trade Review Tab */}
      {tab === "trades" && tradeReview && (
        <div>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-4">
            <div className="border border-[#262626] bg-[#171717] p-3 text-center">
              <p className="font-mono text-xl font-bold text-white">{tradeReview.total_trades}</p>
              <p className="text-[9px] text-[#555] uppercase">Total Trades</p>
            </div>
            <div className="border border-[#262626] bg-[#171717] p-3 text-center">
              <p className="font-mono text-xl font-bold text-[#4ade80]">{tradeReview.fair_trades}</p>
              <p className="text-[9px] text-[#555] uppercase">Fair/Close</p>
            </div>
            <div className="border border-[#262626] bg-[#171717] p-3 text-center">
              <p className="font-mono text-xl font-bold text-[#ff6b6b]">{tradeReview.flagged_trades}</p>
              <p className="text-[9px] text-[#555] uppercase">Flagged</p>
            </div>
            <div className="border border-[#262626] bg-[#171717] p-3 text-center">
              <p className="font-mono text-xl font-bold text-white">{tradeReview.avg_gap}%</p>
              <p className="text-[9px] text-[#555] uppercase">Avg Gap</p>
            </div>
          </div>

          <div className="space-y-2">
            {tradeReview.trades?.map((t, i) => (
              <div key={i} className={`border bg-[#171717] p-3 rounded-sm ${t.verdict === "EXTREME" || t.verdict === "LOPSIDED" ? "border-[#ff6b6b]/30" : "border-[#262626]"}`} data-testid={`trade-review-${i}`}>
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-2">
                    <span className="text-[9px] font-bold px-1.5 py-0.5 rounded-sm" style={{ color: VERDICT_COLORS[t.verdict], backgroundColor: `${VERDICT_COLORS[t.verdict]}15` }}>{t.verdict}</span>
                    <span className="text-[10px] text-[#555]">Week {t.week} · {t.gap_pct}% gap</span>
                  </div>
                  {(t.verdict === "LOPSIDED" || t.verdict === "EXTREME") && <Warning size={14} className="text-[#ff6b6b]" weight="bold" />}
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <p className="text-[9px] text-[#888] mb-1 font-bold">{t.team_a}</p>
                    {t.side_a?.map((p, j) => (
                      <div key={j} className="flex items-center gap-1.5 py-0.5">
                        <PlayerPhoto playerId={p.sleeperId} name={p.name} position={p.position} size={18} />
                        <span className="text-[10px] text-white truncate">{p.name}</span>
                        {p.value > 0 && <span className="text-[9px] font-mono text-[#555] shrink-0">{p.value.toLocaleString()}</span>}
                      </div>
                    ))}
                    <p className="text-[9px] font-mono text-[#ccff00] mt-1">{t.value_a?.toLocaleString()}</p>
                  </div>
                  <div>
                    <p className="text-[9px] text-[#888] mb-1 font-bold">{t.team_b}</p>
                    {t.side_b?.map((p, j) => (
                      <div key={j} className="flex items-center gap-1.5 py-0.5">
                        <PlayerPhoto playerId={p.sleeperId} name={p.name} position={p.position} size={18} />
                        <span className="text-[10px] text-white truncate">{p.name}</span>
                        {p.value > 0 && <span className="text-[9px] font-mono text-[#555] shrink-0">{p.value.toLocaleString()}</span>}
                      </div>
                    ))}
                    <p className="text-[9px] font-mono text-[#ccff00] mt-1">{t.value_b?.toLocaleString()}</p>
                  </div>
                </div>
              </div>
            ))}
            {tradeReview.trades?.length === 0 && <p className="text-xs text-[#555] text-center py-8">No trades found in this league yet.</p>}
          </div>
        </div>
      )}

      {/* League Health Tab */}
      {tab === "health" && health && (
        <div>
          {/* Balance Score */}
          <div className="border border-[#262626] bg-[#171717] p-5 mb-4 text-center">
            <p className="text-[9px] font-bold uppercase text-[#555] mb-1">COMPETITIVE BALANCE SCORE</p>
            <p className={`font-mono text-5xl font-bold ${health.health.competitive_balance >= 70 ? "text-[#4ade80]" : health.health.competitive_balance >= 40 ? "text-[#fbbf24]" : "text-[#ff6b6b]"}`}>{health.health.competitive_balance}</p>
            <p className="text-[10px] text-[#888] mt-1">{health.health.competitive_balance >= 70 ? "Healthy — well-balanced league" : health.health.competitive_balance >= 40 ? "Moderate — some value concentration" : "Low — significant roster imbalance"}</p>
          </div>

          {/* Stats */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-4">
            <div className="border border-[#262626] bg-[#171717] p-3 text-center">
              <p className="font-mono text-lg font-bold text-white">{health.health.avg_value?.toLocaleString()}</p>
              <p className="text-[9px] text-[#555] uppercase">Avg Value</p>
            </div>
            <div className="border border-[#262626] bg-[#171717] p-3 text-center">
              <p className="font-mono text-lg font-bold text-[#4ade80]">{health.health.top_value?.toLocaleString()}</p>
              <p className="text-[9px] text-[#555] uppercase">Strongest</p>
            </div>
            <div className="border border-[#262626] bg-[#171717] p-3 text-center">
              <p className="font-mono text-lg font-bold text-[#ff6b6b]">{health.health.bottom_value?.toLocaleString()}</p>
              <p className="text-[9px] text-[#555] uppercase">Weakest</p>
            </div>
            <div className="border border-[#262626] bg-[#171717] p-3 text-center">
              <p className="font-mono text-lg font-bold text-white">{health.health.value_spread?.toLocaleString()}</p>
              <p className="text-[9px] text-[#555] uppercase">Spread</p>
            </div>
          </div>

          {/* Team Roster Strength Chart */}
          <div className="border border-[#262626] bg-[#171717] p-4 mb-4">
            <h3 className="text-[10px] font-bold uppercase text-[#555] mb-3">ROSTER VALUE BY TEAM</h3>
            <ResponsiveContainer width="100%" height={Math.max(200, health.teams?.length * 32)}>
              <BarChart data={health.teams} layout="vertical" margin={{ left: 10, right: 20 }}>
                <XAxis type="number" hide />
                <YAxis type="category" dataKey="owner" width={100} tick={{ fill: "#888", fontSize: 10 }} axisLine={false} tickLine={false} />
                <Tooltip contentStyle={{ background: "#171717", border: "1px solid #262626", fontSize: 11, color: "#fff" }} formatter={v => v.toLocaleString()} />
                <Bar dataKey="total_value" radius={[0, 3, 3, 0]}>
                  {health.teams?.map((t, i) => (
                    <Cell key={i} fill={i === 0 ? "#ccff00" : i >= health.teams.length - 2 ? "#ff6b6b" : "#333"} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>

          {/* Team Table */}
          <div className="border border-[#262626] bg-[#171717] overflow-hidden">
            <div className="grid grid-cols-5 px-3 py-2 bg-[#0a0a0a] border-b border-[#262626] text-[9px] font-bold uppercase text-[#555]">
              <span className="col-span-2">Team</span>
              <span className="text-right">Value</span>
              <span className="text-right">W-L</span>
              <span className="text-right">Players</span>
            </div>
            {health.teams?.map((t, i) => (
              <div key={i} className="grid grid-cols-5 px-3 py-2 border-b border-[#1a1a1a] items-center">
                <span className="col-span-2 text-xs text-white font-semibold">{i === 0 && <Trophy size={10} weight="fill" className="text-[#ccff00] inline mr-1" />}{t.owner}</span>
                <span className="text-xs font-mono text-right text-[#ccff00]">{t.total_value?.toLocaleString()}</span>
                <span className="text-xs font-mono text-right text-[#888]">{t.wins}-{t.losses}</span>
                <span className="text-xs font-mono text-right text-[#555]">{t.player_count}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
